import React, { FC, SyntheticEvent, useCallback, useState, useEffect } from 'react';
import { ApolloClient, NormalizedCacheObject, useApolloClient } from '@apollo/client';
import { saveResponseAndProcessPayment } from 'api/data/payment';
import WebViewMessageManager from 'utils/webViewMessageManager';
import useWebViewMessages from 'hooks/useWebViewMessages';
import { useClientSecret } from 'contexts/ClientSecret';
import IconButton from '../Button/IconButton';
import PaymentError from '../Error/PaymentError';
import { PaymentErrorProps, PaymentErrorType, PaymentMethodSource, PaymentMethodType } from 'api/data/payment/types';
import { Response } from 'api/data/response/types';
import './style.scss';

interface TapToPayProps {
  onSubmit: (succeeded: boolean) => void;
  pageId?: string;
  response: Response;
  paymentError?: PaymentErrorProps;
  setLoading: (loading: boolean) => void;
  loading: boolean;
  spaceSlug?: string;
  buttonText?: string;
}

type CompatibilityStatus = 'hide_tap_to_pay' | 'update_required_ios';

type WebViewMessage = {
  status: 'payment_failed' | 'payment_cancelled' | 'payment_succeeded' | CompatibilityStatus;
  message?: string;
};

const IOS_UPDATE_MESSAGE =
  'Your iOS version is not supported for Tap to Pay. Please update your device to the latest iOS version to continue using this feature.';
const PROCESS_PAYMENT_ERROR_MESSAGE = 'Process payment: something went wrong';

const TapToPay: FC<TapToPayProps> = ({ onSubmit, pageId, response, paymentError, setLoading, loading }) => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [processPaymentSucceeded, setProcessPaymentSucceeded] = useState<boolean>(false);
  const [paymentResult, setPaymentResult] = useState<WebViewMessage>();
  const [tapToPayCompatibility, setTapToPayCompatibility] = useState<CompatibilityStatus>();

  const { clientSecret, setClientSecret } = useClientSecret();
  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;

  useWebViewMessages<WebViewMessage>(message => {
    switch (message.status) {
      case 'payment_failed':
      case 'payment_cancelled':
      case 'payment_succeeded':
        setPaymentResult(message);
        break;
      case 'hide_tap_to_pay':
      case 'update_required_ios':
        setTapToPayCompatibility(message.status);
        break;
    }
  });

  useEffect(() => {
    if (paymentResult?.status === 'payment_succeeded') {
      onSubmit(processPaymentSucceeded);
    } else if (paymentResult?.status === 'payment_failed') {
      setErrorMessage(paymentResult?.message ?? PROCESS_PAYMENT_ERROR_MESSAGE);
      setLoading(false);
    } else if (paymentResult?.status === 'payment_cancelled') {
      setLoading(false);
    }
  }, [onSubmit, paymentResult, processPaymentSucceeded, setLoading]);

  const handleCompatibilityDeviceError = () => {
    if (tapToPayCompatibility === 'update_required_ios') {
      setErrorMessage(IOS_UPDATE_MESSAGE);
      setLoading(false);
      return true;
    }
    return false;
  };

  const processPaymentAndTriggerMobile = useCallback(async () => {
    try {
      let currentClientSecret = clientSecret;

      if (!currentClientSecret) {
        const processPaymentData = {
          paymentMethodType: PaymentMethodType.CARD,
          paymentMethodSource: PaymentMethodSource.TAP_TO_PAY,
          response,
        };

        const { succeeded, clientSecret: newSecret } = await saveResponseAndProcessPayment(
          client,
          response,
          processPaymentData,
        );
        setProcessPaymentSucceeded(succeeded);

        if (!newSecret) {
          setErrorMessage(PROCESS_PAYMENT_ERROR_MESSAGE);
          setLoading(false);
          return;
        }

        currentClientSecret = newSecret;
        setClientSecret(currentClientSecret);
      }

      WebViewMessageManager.getInstance().sendMessage('charge_tap_to_pay', { clientSecret: currentClientSecret });
    } catch (error) {
      setErrorMessage((error as PaymentErrorType).message);
      setLoading(false);
    }
  }, [client, clientSecret, response, setLoading, setClientSecret]);

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage('');

    if (handleCompatibilityDeviceError()) return;

    await processPaymentAndTriggerMobile();
  };

  const isSubmitDisabled = loading || (!!pageId && response.order.totalCents === 0);

  return (
    <div className="tap-to-pay-container">
      {!loading && errorMessage && <PaymentError title={paymentError?.title} errorMessage={errorMessage} />}
      <form
        onSubmit={event => void handleSubmit(event)}
        className="payment-method"
        aria-label="tap to pay payment form">
        <IconButton
          type="submit"
          disabled={isSubmitDisabled}
          icon="tap_to_pay_white"
          className="row justify-center gap-s">
          <span className="row align-center subtitle-small">{loading ? 'Processing...' : 'Tap to Pay on iPhone'}</span>
        </IconButton>
      </form>
    </div>
  );
};

export default TapToPay;
